import { AppNexusProps } from '@components/AppNexus'
import { RecommendedItem, useRecommendations } from '@hooks/useRecommendations'
import useRenderer from '@hooks/useRenderer'
import translate from '@i18n'
import { mobileCSS } from '@measures/responsive'
import { AppNexusWidgetProps } from '@widgets/AppNexus'
import { CookWidget } from '@widgets/types'
import {
  FunctionComponent,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import styled, { css } from 'styled-components'
import { printCSS } from '@utils/style'

export interface RecommendationProps {
  content?: (RecommendedItem | AppNexusProps)[]
}

const StyledTitle = styled.div`
  ${({
    theme: {
      fonts: { goodOTNarrow },
      colors: {
        grey: { grey_100 },
      },
    },
  }) => {
    return css`
      font-size: 24px;
      font-family: ${goodOTNarrow};
      width: 100%;
      font-weight: 600;
      color: ${grey_100};
      margin-bottom: 20px;
      ${mobileCSS(css`
        font-size: 22px;
        margin-bottom: 10px;
      `)}
    `
  }}
`

const StyledRecommendations = styled.div`
  display: grid;
  grid-gap: 20px;
  margin-top: 30px;

  ${printCSS(css`
    display: none;
  `)}
`

const RecommendationsWrapper = styled.div`
  display: grid;
  grid-gap: 20px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  ${mobileCSS(css`
    grid-template-columns: minmax(0, 1fr);
    > *:nth-child(4) {
      display: none;
    }
  `)}
`

const Recommendations: FunctionComponent<RecommendationProps> = (props) => {
  const { content: propsContent = [] } = props

  const Renderer = useRenderer()

  const defaultData = useMemo<RecommendedItem[]>(
    () => [
      ...(propsContent.slice(0, 4) as RecommendedItem[]),
      ...(propsContent.slice(5, 9) as RecommendedItem[]),
    ],
    [propsContent]
  )

  const [ad1] = useState<ReactElement>(() => (
    <Renderer>{propsContent[4] as AppNexusWidgetProps}</Renderer>
  ))

  const [ad2] = useState<ReactElement>(() => (
    <Renderer>{propsContent[9] as AppNexusWidgetProps}</Renderer>
  ))

  const [recommendationsPart1, setRecommendationsPart1] = useState<
    RecommendedItem[]
  >(() => propsContent.slice(0, 4) as RecommendedItem[])
  const [recommendationsPart2, setRecommendationsPart2] = useState<
    RecommendedItem[]
  >(() => propsContent.slice(5, 9) as RecommendedItem[])

  const { data: apiRecommendationData } = useRecommendations({
    defaultData,
  })

  const joinedRecoArticleIds = useCallback((data: RecommendedItem[]) => {
    return data
      .map((item: RecommendedItem) => item.articleId)
      .filter((itemArticleId: string) => itemArticleId !== undefined)
      .join(',')
  }, [])

  /**
   * the initial index is passed to calculate the
   * recommendedPosition irrelevant of the separation in 2 groups
   */
  const adjustedRecommentedItems = useCallback(
    (data: RecommendedItem[], initialIndex: number) => {
      return data.map((item: RecommendedItem, index: number) => {
        return {
          recommendationData: {
            position: initialIndex + index,
            articleIds: joinedRecoArticleIds(data),
            correlationId: item?.correlationId ?? '',
            aureusOfferId: item.aureusOfferId ?? '',
          },
          ...item,
        }
      })
    },
    [joinedRecoArticleIds]
  )

  useEffect(() => {
    if (apiRecommendationData?.length === 8) {
      setRecommendationsPart1(
        adjustedRecommentedItems(apiRecommendationData?.slice(0, 4), 0)
      )
      setRecommendationsPart2(
        adjustedRecommentedItems(apiRecommendationData?.slice(4, 8), 4)
      )
    }
  }, [apiRecommendationData, adjustedRecommentedItems])

  if (propsContent.length !== 10) {
    return null
  }

  return (
    <StyledRecommendations>
      <StyledTitle className="container-title">
        {translate('recommendation.title')}
      </StyledTitle>
      <RecommendationsWrapper>
        {recommendationsPart1.map((recommendedItem) => (
          <Renderer key={recommendedItem.articleId}>{recommendedItem}</Renderer>
        ))}
      </RecommendationsWrapper>
      {ad1}
      <RecommendationsWrapper>
        {recommendationsPart2.map((recommendedItem) => (
          <Renderer key={recommendedItem.articleId}>{recommendedItem}</Renderer>
        ))}
      </RecommendationsWrapper>
      {ad2}
    </StyledRecommendations>
  )
}

const widget: CookWidget = {
  kind: ['widget', 'recommendations'],
  component: Recommendations,
}

export default widget
